import {
  formatEur,
  monthlyEquivalentOf,
  Valuation,
  type MemberDto,
  type RecurrenceIntervalValue,
} from "@gladis/shared";
import { ActionMenu, EmptyState, PersonLabel, Table } from "@gladis/ui";
import { incomeCategoryLabel, useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { IncomeSourceDto } from "../dto.js";
import { intervalLabel } from "../intervals.js";

/**
 * The household's regular income, as a table.
 *
 * ==========================================================================================
 * WHY THE COLUMNS ARE THESE COLUMNS
 * ==========================================================================================
 *
 * Name, person, category, from, amount, monthly, until. Every one of them is a fact a member sorts or
 * scans by, and two of them are the ones this module exists to make legible:
 *
 *   FROM     the source's start, derived from its earliest amount — not a stored field, and the
 *            thing a household changes when it backfills.
 *   UNTIL    `ended_on`, empty while the income is still arriving. An ended source stays in the
 *            table with a date rather than disappearing, because the years it was earned are still
 *            in the chart and a row that vanished would make them unexplainable.
 *
 * AMOUNT AND MONTHLY ARE BOTH SHOWN, and they are different questions. "€1,000 weekly" is what the
 * household is paid; "€4,333.33" is what that comes to per month and what every total on the page is
 * built from. Showing only the first makes the stat card unverifiable; showing only the second hides
 * what actually arrives.
 *
 * ==========================================================================================
 * THE ACTIONS ARE BEHIND ONE CONTROL
 * ==========================================================================================
 *
 * Four buttons in the last cell made the column as wide as "It stopped" plus "Remove" and put the
 * row's controls in competition with its data. One trigger keeps the row scannable AND lets the
 * labels stay long enough to mean something — which matters more here than in most tables, because
 * "It stopped" and "Remove the record" have opposite consequences for years already elapsed.
 */

/** The monthly equivalent, through the kernel's single implementation. */
function monthlyOf(source: IncomeSourceDto): number | null {
  if (source.currentAmountInEuros === null || source.recurrenceInterval === null) return null;
  const at = new Date();
  const amount = Valuation.fromCents(Math.round(source.currentAmountInEuros * 100), at);
  return monthlyEquivalentOf(
    amount,
    source.recurrenceInterval as RecurrenceIntervalValue,
  ).toEuros();
}

export interface SourceTableProps {
  sources: readonly IncomeSourceDto[];
  /** The household's members, for their faces (020). */
  members?: readonly MemberDto[];
  onEdit: (source: IncomeSourceDto) => void;
  onHistory: (source: IncomeSourceDto) => void;
  onEnd: (source: IncomeSourceDto) => void;
  onResume: (source: IncomeSourceDto) => void;
  onArchive: (source: IncomeSourceDto) => void;
}

export function SourceTable({
  sources,
  members = [],
  onEdit,
  onHistory,
  onEnd,
  onResume,
  onArchive,
}: SourceTableProps): ReactNode {
  const t = useT();
  if (sources.length === 0) {
    return <EmptyState>{t("inc.source.empty")}</EmptyState>;
  }

  // Active first, then ended — most recent ending first, because a job left last year is more
  // interesting than one left in 2019.
  const ordered = [...sources].sort((left, right) => {
    if ((left.endedOn === null) !== (right.endedOn === null)) return left.endedOn === null ? -1 : 1;
    if (left.endedOn && right.endedOn) return right.endedOn.localeCompare(left.endedOn);
    return left.name.localeCompare(right.name);
  });

  return (
    <Table aria-label={t("inc.source.heading")}>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>{t("inc.source.colIncome")}</Table.HeaderCell>
          <Table.HeaderCell>{t("inc.source.colWho")}</Table.HeaderCell>
          <Table.HeaderCell>{t("inc.source.category")}</Table.HeaderCell>
          <Table.HeaderCell>{t("inc.source.from")}</Table.HeaderCell>
          <Table.HeaderCell>{t("inc.source.colUntil")}</Table.HeaderCell>
          <Table.HeaderCell numeric>{t("inc.source.amount")}</Table.HeaderCell>
          <Table.HeaderCell numeric>{t("inc.source.colPerMonth")}</Table.HeaderCell>
          {/* The header is empty by design, but a screen reader still needs the column named. */}
          <Table.HeaderCell>
            <span className="sr-only">{t("inc.source.actions")}</span>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Head>

      <Table.Body>
        {ordered.map((source) => {
          const monthly = monthlyOf(source);
          const ended = source.endedOn !== null;

          return (
            <Table.Row key={source.id}>
              <Table.Cell>
                <span className={ended ? "text-muted" : "font-medium"}>{source.name}</span>
              </Table.Cell>
              <Table.Cell>
                {/* Joint is the household, and "Former member" is a stand-in — neither is a person. */}
                <PersonLabel
                  label={
                    source.ownership === "joint"
                      ? t("person.joint")
                      : (source.ownerLabel ?? t("person.formerMember"))
                  }
                  isPerson={source.ownership !== "joint" && source.ownerLabel !== null}
                  avatarUrl={members.find((m) => m.userId === source.ownerId)?.avatarUrl}
                  // Joint income is the household's; "Former member" is nobody's. Only the first
                  // gets the group, which is the distinction `isPerson` alone could not draw.
                  {...(source.ownership === "joint"
                    ? {
                        group: members.map((m) => ({
                          name: m.displayName,
                          avatarUrl: m.avatarUrl,
                        })),
                      }
                    : {})}
                />
              </Table.Cell>
              <Table.Cell>{incomeCategoryLabel(source.category)}</Table.Cell>
              <Table.Cell>{source.startedOn ?? "—"}</Table.Cell>
              <Table.Cell>
                {/* Empty while it is still arriving. A dash rather than "ongoing": the column is
                    already named "Until", and a word there reads as a value. */}
                {source.endedOn ?? "—"}
              </Table.Cell>
              <Table.Cell numeric>
                {source.currentAmountInEuros === null || source.recurrenceInterval === null ? (
                  "—"
                ) : (
                  <>
                    {formatEur(source.currentAmountInEuros)}
                    <span className="ml-1 text-muted">
                      {intervalLabel(
                        source.recurrenceInterval as RecurrenceIntervalValue,
                      ).toLowerCase()}
                    </span>
                  </>
                )}
              </Table.Cell>
              <Table.Cell numeric>{monthly === null ? "—" : formatEur(monthly)}</Table.Cell>
              <Table.Cell>
                <ActionMenu
                  label={`Actions for ${source.name}`}
                  items={[
                    { label: t("inc.source.amountHistory"), onSelect: () => onHistory(source) },
                    { label: t("inc.source.editDetails"), onSelect: () => onEdit(source) },
                    ended
                      ? { label: t("inc.source.itResumed"), onSelect: () => onResume(source) }
                      : { label: t("inc.source.itStopped"), onSelect: () => onEnd(source) },
                    {
                      label: t("inc.source.removeRecord"),
                      onSelect: () => onArchive(source),
                      destructive: true,
                    },
                  ]}
                />
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
}
